import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  serverTimestamp, 
  query, 
  where, 
  getDocs, 
  getDoc,
  DocumentReference, 
  updateDoc, 
  increment 
} from 'firebase/firestore';
import { Registration } from '../types';
import Handlebars from 'handlebars';
import { renderEmailTemplate } from './renderEmailTemplate';
import { registerDefaultEmailHelpers } from './handlebarsHelpers';
import { getEmailTemplate } from './templateService';
import { getEventEdition } from './eventEditionService';
import { formatShortDate, formatDateTime } from '../utils/dateFormatter';
import { validateAndReportMissingVariables } from './emailValidationService';
import { debugLog } from '../utils/debugLogger';
import { logger } from './loggingService';
import { 
  buildEmailContext, 
  compareContexts, 
  logContextMismatches,
  EmailContext 
} from './emailContextBuilder';
import { resolveBoilerplates, hasBoilerplateHandlebars } from './boilerplateResolver';

// Status translations for email templates
const statusTranslations = {
  en: {
    pending: 'Pending',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
    expired: 'Expired'
  },
  'nb-NO': {
    pending: 'Ubekreftet',
    confirmed: 'Bekreftet',
    cancelled: 'Avlyst',
    expired: 'Utløpt'
  },
  no: {
    pending: 'Ubekreftet',
    confirmed: 'Bekreftet',
    cancelled: 'Avlyst',
    expired: 'Utløpt'
  },
  fr: {
    pending: 'En attente',
    confirmed: 'Confirmée',
    cancelled: 'Annulée',
    expired: 'Expirée'
  }
};

/**
 * Gets the localized display name for a registration status
 */
export function getStatusDisplayName(status: string, language: string = 'en'): string {
  const lang = language === 'nb-NO' ? 'nb-NO' : (language === 'no' ? 'no' : language);
  const translations = statusTranslations[lang as keyof typeof statusTranslations] || statusTranslations.en;
  return translations[status as keyof typeof translations] || status;
}

 const DEFAULT_STATIONARY_IMAGE_URL = 'https://krultra.no/assets/email/krultra_stationary_top.png';

// Helper function to get user language preference
async function getUserLanguagePreference(email: string): Promise<string> {
  try {
    const db = getFirestore();
    const usersQuery = query(collection(db, 'users'), where('email', '==', email));
    const userSnap = await getDocs(usersQuery);
    
    if (!userSnap.empty) {
      const user = userSnap.docs[0].data();
      return user.languagePreference || 'en'; // Default to English
    }
    
    return 'en'; // Default for non-registered users
  } catch (error) {
    console.warn('Error getting user language preference, defaulting to English:', error);
    return 'en';
  }
}

/**
 * Email types supported by the application
 */
export enum EmailType {
  WELCOME = 'welcome',
  WELCOME_PENDING_PAYMENT = 'welcome_pending_payment',
  REGISTRATION_UPDATE = 'registration_update',
  REGISTRATION_UPDATE_PAYMENT_NEEDED = 'registration_update_payment_needed',
  PAYMENT_CONFIRMATION = 'payment_confirmation',
  NEWSLETTER = 'newsletter',
  REMINDER = 'reminder',
  LAST_NOTICE = 'lastNotice',
  WAITING_LIST_REGISTRATION = 'waiting_list_registration',
  WAITING_LIST_CONFIRMATION = 'waiting_list_confirmation',
  CANCELLATION = 'registration_cancellation',
  EXPIRATION = 'registration_expiration',
  STATUS_CHANGED = 'status_changed',
  P_LIST2W_LIST = 'p-list2w-list',
  W_LIST2P_LIST_OFFER = 'w-list2p-list_offer',
  REFUND = 'refund',
  REFUND_UNDER_VALIDATION = 'refund_under_validation',
  REFUND_VALIDATED = 'refund_validated',
  DEPOSIT_REFUND = 'deposit_refund',
  REFUND_DECLINED = 'refund_declined',
}



/**
 * Sends a welcome email to a new registrant
 * Context building (localization, payment fields, etc.) is handled by sendEmail via buildEmailContext
 * @param registration The registration data
 */
export const sendWelcomeEmail = async (registration: Registration): Promise<DocumentReference<any>> => {
  return sendEmail(EmailType.WELCOME, registration.email, registration);
};

/**
 * Sends a welcome email for registrations with pending payment
 * Context building (localization, payment fields, etc.) is handled by sendEmail via buildEmailContext
 * @param registration The registration data
 */
export const sendWelcomePendingPaymentEmail = async (registration: Registration): Promise<DocumentReference<any>> => {
  return sendEmail(EmailType.WELCOME_PENDING_PAYMENT, registration.email, registration);
};

/**
 * Sends an email when a registration is updated
 * Context building (localization, payment fields, etc.) is handled by sendEmail via buildEmailContext
 * @param registration The updated registration data
 */
export const sendRegistrationUpdateEmail = async (registration: Registration): Promise<DocumentReference<any>> => {
  return sendEmail(EmailType.REGISTRATION_UPDATE, registration.email, registration);
};

/**
 * Sends a registration update payment needed email (when payment fails for an update)
 * @param registration The registration data
 */
export const sendRegistrationUpdatePaymentNeededEmail = async (registration: Registration): Promise<DocumentReference<any>> => {
  return sendEmail(EmailType.REGISTRATION_UPDATE_PAYMENT_NEEDED, registration.email, registration);
};

/**
 * Sends a payment confirmation email
 * Context building (localization, payment fields, etc.) is handled by sendEmail via buildEmailContext
 * @param registration The registration data
 */
export const sendPaymentConfirmationEmail = async (registration: Registration): Promise<DocumentReference<any>> => {
  return sendEmail(EmailType.PAYMENT_CONFIRMATION, registration.email, registration);
};

/**
 * Sends waiting-list confirmation email
 * Context building (localization, payment fields, etc.) is handled by sendEmail via buildEmailContext
 */
export const sendWaitingListEmail = async (registration: Registration): Promise<DocumentReference<any>> => {
  return sendEmail(EmailType.WAITING_LIST_CONFIRMATION, registration.email, registration);
};

/**
 * Sends an initial waiting-list registration email to a user.
 * Context building (localization, payment fields, etc.) is handled by sendEmail via buildEmailContext
 */
export const sendWaitingListRegistrationEmail = async (registration: Registration): Promise<DocumentReference<any>> => {
  return sendEmail(EmailType.WAITING_LIST_REGISTRATION, registration.email, registration);
};

/**
 * Sends registration cancellation email
 * Context building (localization, payment fields, etc.) is handled by sendEmail via buildEmailContext
 */
export const sendRegistrationCancellationEmail = async (registration: Registration): Promise<DocumentReference<any>> => {
  return sendEmail(EmailType.CANCELLATION, registration.email, registration);
};

/**
 * Sends registration expiration email
 * Context building (localization, payment fields, etc.) is handled by sendEmail via buildEmailContext
 */
export const sendRegistrationExpirationEmail = async (registration: Registration): Promise<DocumentReference<any>> => {
  return sendEmail(EmailType.EXPIRATION, registration.email, registration);
};

/**
 * Sends status-changed email
 * Context building (localization, payment fields, etc.) is handled by sendEmail via buildEmailContext
 */
export const sendStatusChangedEmail = async (registration: Registration): Promise<DocumentReference<any>> => {
  return sendEmail(EmailType.STATUS_CHANGED, registration.email, registration);
};

/**
 * Generic email sender using templates
 * 
 * IMPORTANT: This function now uses buildEmailContext() to ensure consistent context.
 * The providedContext parameter is kept for backwards compatibility and comparison checks.
 * 
 * @param type - Email template type
 * @param to - Recipient email address
 * @param providedContext - Optional context (used for comparison, builder output takes precedence)
 */
async function sendEmail(type: EmailType, to: string, providedContext: any = {}): Promise<DocumentReference<any>> {
  const db = getFirestore();
  
  // ============================================================================
  // PHASE 2: Use centralized context builder
  // The builder handles all localization, formatting, and variable mapping
  // We keep providedContext for comparison checks to detect any mismatches
  // ============================================================================
  
  // Build context using the centralized builder
  // This ensures consistent handling across all email types
  let builderContext: Partial<EmailContext> = {};
  try {
    builderContext = await buildEmailContext(providedContext);
    
    // Compare builder output with provided context to detect mismatches
    // This helps catch cases where the builder might be missing something
    const mismatches = compareContexts(builderContext, providedContext);
    if (Object.keys(mismatches).length > 0) {
      logContextMismatches(mismatches, type);
      // TODO: [LOGGING-SYSTEM] Write mismatches to logs collection when available
    }
  } catch (buildError) {
    // TODO: [LOGGING-SYSTEM] Log this error when logging system is implemented
    console.error('[sendEmail] Error building context, falling back to provided context:', buildError);
    // Fall back to provided context if builder fails
    builderContext = providedContext;
  }
  
  // Merge contexts: builder output takes precedence, but include any extra fields from providedContext
  const context = {
    ...providedContext,  // Start with provided context (includes any extra fields)
    ...builderContext,   // Override with builder output (canonical values)
  };
  
  // Get the event edition from the registration context if available
  const eventEditionId = (context as any).editionId || (context as any).eventEditionId;
  let eventEdition = null;
  let eventName = context.eventName || '';
  let eventShortName = context.eventShortName || '';
  let edition = context.eventEdition || '';
  
  // Only fetch event edition if not already in builder context
  if (eventEditionId && !eventName) {
    try {
      eventEdition = await getEventEdition(eventEditionId);
      if (eventEdition) {
        eventName = eventEdition.eventName || '';
        eventShortName = eventEdition.eventShortName || '';
        edition = eventEdition.edition?.toString() || '';
      }
    } catch (e) {
      console.error('[sendEmail] Error fetching eventEdition', e);
      // Don't throw error for general emails, just log it
      if (type !== EmailType.NEWSLETTER) {
        throw e;
      }
    }
  } else if (!eventEditionId && type !== EmailType.NEWSLETTER) {
    // Only throw error for non-newsletter emails that require an event edition
    throw new Error('No event edition ID found in registration data');
  }
  let tpl;
  let usedLocale = 'en'; // Default fallback
  
  // First try to get language preference from context (builder should have set this)
  let userLanguagePreference = context.languagePreference;
  
  // If not in context, fall back to fetching from user document
  if (!userLanguagePreference) {
    userLanguagePreference = await getUserLanguagePreference(to);
  }
  
  try {
    // Ensure helpers are registered prior to compiling
    registerDefaultEmailHelpers(Handlebars);
    
    // Try templates in order of preference: user preference -> English -> Norwegian
    const preferredLocales = [userLanguagePreference, 'en', 'no'];
    
    for (const locale of preferredLocales) {
      try {
        const template = await getEmailTemplate(type, locale);
        
        // Check if template has actual content (not just empty stub)
        if (template && 
            typeof template.subjectTemplate === 'string' &&
            template.subjectTemplate.trim() !== '' &&
            typeof template.bodyTemplate === 'string' &&
            template.bodyTemplate.trim() !== '') {
          tpl = template;
          usedLocale = locale;
          break; // Found valid template
        }
      } catch (e) {
        continue; // Try next locale
      }
    }
    
    if (!tpl) {
      console.error(`[sendEmail] No valid template found for type ${type} in any preferred locale: ${preferredLocales.join(', ')}`);
      throw new Error(`No valid template found for type ${type} in any preferred locale`);
    }
  } catch (e) {
    console.error('[sendEmail] Error fetching email template', e);
    throw e;
  }
  // enrich context for subject/body templates
  const stationaryImageUrl =
    process.env.REACT_APP_KRULTRA_STATIONARY_IMAGE_URL ||
    (process.env as any).KRULTRA_STATIONARY_IMAGE_URL ||
    DEFAULT_STATIONARY_IMAGE_URL;

  const enrichedContext = {
    ...context,
    eventName,
    eventShortName,
    eventEdition: edition,
    krultraStationaryImageUrl: stationaryImageUrl,
    headerImageUrl:
      process.env.REACT_APP_KRULTRA_HEADER_IMAGE_URL ||
      (process.env as any).KRULTRA_HEADER_IMAGE_URL ||
      stationaryImageUrl,
    logoUrl:
      process.env.REACT_APP_KRULTRA_LOGO_URL ||
      (process.env as any).KRULTRA_LOGO_URL ||
      stationaryImageUrl,
    today: formatShortDate(context.today || new Date()),
    // Format dates directly in the context for use in templates
    dateOfBirth: context.dateOfBirth ? formatShortDate(context.dateOfBirth) : '',
    waitinglistExpires: context.waitinglistExpires ? formatShortDate(context.waitinglistExpires) : '',
    updatedAt: context.updatedAt ? formatDateTime(context.updatedAt) : ''
  };

  // Determine if this registration is flagged as test data and apply redirect if needed
  const testRedirectTo = process.env.REACT_APP_TEST_DATA_REDIRECT_TO || 'test@krultra.no';
  const originalRecipient = to;
  let isTestData = Boolean((context as any).isTestData ?? (providedContext as any)?.isTestData);

  if (!isTestData && context.id) {
    try {
      const registrationSnap = await getDoc(doc(db, 'registrations', context.id));
      if (registrationSnap.exists() && registrationSnap.data()?.isTestData) {
        isTestData = true;
      }
    } catch (lookupError) {
      console.warn('[sendEmail] Failed to check registration for test-data flag:', lookupError);
    }
  };

  if (isTestData) {
    (enrichedContext as any).originalRecipient = originalRecipient;
    (enrichedContext as any).redirectedTo = testRedirectTo;
    (enrichedContext as any).isTestData = true;
  }

  const resolvedRecipient = isTestData ? testRedirectTo : originalRecipient;

  // Debug: Log the stationary image URL
  debugLog('[sendEmail] Stationary image URL', { 
    url: enrichedContext.krultraStationaryImageUrl,
    envVar: process.env.REACT_APP_KRULTRA_STATIONARY_IMAGE_URL 
  });
  
  // Validate template variables and create admin task if needed
  // Skip validation for operational/administrative emails to avoid noise
  const skipValidationForTypes = [
    EmailType.REFUND_DECLINED,
    EmailType.REFUND_VALIDATED,
    EmailType.REFUND_UNDER_VALIDATION,
    EmailType.CANCELLATION,
    EmailType.EXPIRATION
  ];
  
  if (!skipValidationForTypes.includes(type)) {
    // This runs asynchronously and doesn't block email sending
    validateAndReportMissingVariables(type, usedLocale, enrichedContext, to).catch(error => {
      console.error('[sendEmail] Error during template validation:', error);
    });
  }
  if (!tpl.subjectTemplate) {
    console.error(`[sendEmail] No subject template found for type ${type}`);
    throw new Error(`No subject template found for type ${type}`);
  }
  
  // ============================================================================
  // BOILERPLATE RESOLUTION: Resolve {{>boilerplateName}} handlebars before compilation
  // This allows templates to reference reusable boilerplate content
  // ============================================================================
  let resolvedSubjectTemplate = tpl.subjectTemplate;
  let resolvedBodyTemplate = tpl.bodyTemplate;
  
  try {
    // Resolve boilerplates in subject template if it contains any
    if (hasBoilerplateHandlebars(tpl.subjectTemplate)) {
      const subjectResult = await resolveBoilerplates(tpl.subjectTemplate, {
        locale: usedLocale,
        onMissingBoilerplate: 'warn'
      });
      resolvedSubjectTemplate = subjectResult.resolvedContent;
      if (subjectResult.warnings.length > 0) {
        console.warn(`[sendEmail] Boilerplate warnings in subject for ${type}:`, subjectResult.warnings);
      }
      if (subjectResult.errors.length > 0) {
        console.error(`[sendEmail] Boilerplate errors in subject for ${type}:`, subjectResult.errors);
      }
      if (subjectResult.resolvedBoilerplates.length > 0) {
        debugLog('[sendEmail] Resolved boilerplates in subject', { 
          type, 
          boilerplates: subjectResult.resolvedBoilerplates 
        });
      }
    }
    
    // Resolve boilerplates in body template if it contains any
    if (tpl.bodyTemplate && hasBoilerplateHandlebars(tpl.bodyTemplate)) {
      const bodyResult = await resolveBoilerplates(tpl.bodyTemplate, {
        locale: usedLocale,
        onMissingBoilerplate: 'warn'
      });
      resolvedBodyTemplate = bodyResult.resolvedContent;
      if (bodyResult.warnings.length > 0) {
        console.warn(`[sendEmail] Boilerplate warnings in body for ${type}:`, bodyResult.warnings);
      }
      if (bodyResult.errors.length > 0) {
        console.error(`[sendEmail] Boilerplate errors in body for ${type}:`, bodyResult.errors);
      }
      if (bodyResult.resolvedBoilerplates.length > 0) {
        debugLog('[sendEmail] Resolved boilerplates in body', { 
          type, 
          boilerplates: bodyResult.resolvedBoilerplates 
        });
      }
    }
  } catch (boilerplateError) {
    // Log error but continue with original templates (graceful degradation)
    console.error(`[sendEmail] Error resolving boilerplates for ${type}:`, boilerplateError);
    // Templates will be used as-is, with {{>name}} rendered as literal text
  }
  
  let subject: string;
  try {
    subject = renderEmailTemplate(resolvedSubjectTemplate, enrichedContext);
  } catch (err) {
    console.error(`[sendEmail] Error compiling subject template for ${type}:`, err);
    throw err;
  }

  if (!tpl.bodyTemplate) {
    console.error(`[sendEmail] No body template found for type ${type}`);
    throw new Error(`No body template found for type ${type}`);
  }
  let html: string;
  try {
    html = renderEmailTemplate(resolvedBodyTemplate, enrichedContext);
  } catch (err) {
    console.error(`[sendEmail] Error compiling body template for ${type}:`, err);
    throw err;
  }

  // Create the email document (compatible with SMTP agent reading /mail)
  const mailDoc = {
    // Required by SMTP agent
    to: resolvedRecipient,
    replyTo: 'support@krultra.no', // Set reply-to to support email
    subject, // duplicate subject at top-level
    message: {
      subject, // keep nested for backward compatibility
      html,
    },
    // Initialize agent namespace so listener query can see this doc
    smtpAgent: {
      state: 'PENDING' as const,
      lastUpdatedAt: serverTimestamp(),
    },
    // Agent state fields
    state: 'PENDING' as const,
    delivery: null as any,
    // App metadata
    type,
    context: enrichedContext,
    registrationId: enrichedContext.id || null,
    createdAt: serverTimestamp(),
    status: 'pending', // keep legacy field for compatibility
    // Add metadata for tracking and filtering
    metadata: {
      emailType: type,
      locale: usedLocale, // Actually used locale
      userLanguagePreference, // User's preferred language
      emailTemplate: `${type}_${usedLocale}`,
      ...(isTestData && {
        isTestData: true,
        originalRecipient,
        redirectedTo: testRedirectTo,
      }),
      ...(eventEditionId && {
        eventEditionId,
        eventId: eventEditionId.split('-').slice(0, -1).join('-'), // Remove year suffix (e.g., 'kutc-2025' -> 'kutc')
        edition: edition?.toString() || ''
      })
    },
  };

  if (isTestData) {
    console.log('[sendEmail] Redirecting test-data email', {
      type,
      originalRecipient,
      redirectedTo: resolvedRecipient,
      registrationId: enrichedContext.id
    });
  }

  // Write to mail collection in Firestore
  let mailRef;
  try {
    mailRef = await addDoc(collection(db, 'mail'), mailDoc);
  } catch (firestoreError) {
    console.error('[sendEmail] Error writing mailDoc to Firestore:', firestoreError);
    throw firestoreError;
  }

  // update registration counters for reminders and last notices
  if (enrichedContext.id) {
    const regRef = doc(db, 'registrations', enrichedContext.id);
    try {
      if (type === EmailType.REMINDER) {
        await updateDoc(regRef, { remindersSent: increment(1) });
      } else if (type === EmailType.LAST_NOTICE) {
        await updateDoc(regRef, { lastNoticesSent: increment(1) });
      }
    } catch (err) {
      console.error(`[sendEmail] Error incrementing counters for ${enrichedContext.id}`, err);
    }
  }
  return mailRef;
}

// Generic sender for dynamic templates
export async function enqueueRawEmail(
  to: string,
  subject: string,
  html: string,
  options?: {
    type?: EmailType | string;
    context?: any;
    eventEditionId?: string;
    campaignId?: string;
    campaignType?: 'transactional' | 'notifyFutureEvents' | 'sendRunningOffers';
    unsubscribeUrl?: string;
  }
): Promise<DocumentReference<any>> {
  const db = getFirestore();
  const type = options?.type ?? 'test';
  const context = options?.context ?? {};
  const eventEditionId = options?.eventEditionId;

  // Build metadata similar to sendEmail
  let eventId = undefined as string | undefined;
  let edition = undefined as string | undefined;
  if (eventEditionId) {
    try {
      const ee = await getEventEdition(eventEditionId);
      if (ee) {
        edition = ee.edition?.toString() || '';
        eventId = eventEditionId.split('-').slice(0, -1).join('-');
      }
    } catch (error) {
      await logger.warn(
        '[enqueueRawEmail] Failed to load event edition metadata',
        { eventEditionId, emailType: type },
        { error, attentionRequired: false }
      );
    }
  }

  const mailDoc = {
    to,
    replyTo: 'support@krultra.no', // Set reply-to to support email
    subject,
    message: {
      subject,
      html,
    },
    smtpAgent: {
      state: 'PENDING' as const,
      lastUpdatedAt: serverTimestamp(),
    },
    state: 'PENDING' as const,
    delivery: null as any,
    type,
    context,
    registrationId: context?.id || null,
    createdAt: serverTimestamp(),
    status: 'pending',
    ...(options?.campaignType && { campaignType: options.campaignType }),
    ...(options?.unsubscribeUrl && { unsubscribeUrl: options.unsubscribeUrl }),
    metadata: {
      emailType: String(type),
      locale: context?.locale || 'en',
      emailTemplate: `${type}_${context?.locale || 'en'}`,
      ...(options?.campaignId && { campaignId: options.campaignId }),
      ...(eventEditionId && {
        eventEditionId,
        eventId: eventId || '',
        edition: edition || '',
      }),
    },
  };

  return addDoc(collection(db, 'mail'), mailDoc);
}

export { sendEmail };
